import {
  Button,
  Chip,
  Chips,
  Group,
  Stack,
  TextInput,
  Text,
  NumberInput,
  Tabs,
  Select,
  Code,
  Textarea,
} from '@mantine/core';
import { useForm } from '@mantine/hooks';
import { useModals } from '@mantine/modals';
import { History, Settings, Slideshow } from 'tabler-icons-react';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import {
  memoryPurged,
  selectHistoryIds,
  selectClipLimit,
  selectLayout,
  selectProviders,
} from '../clips/clipQueueSlice';
import {
  selectChannel,
  selectCommandPrefix,
  selectIgnoredChatters,
  selectInitialQueueOpen,
  settingsChanged,
} from './settingsSlice';

function SettingsModal({ closeModal }: { closeModal: () => void }) {
  const dispatch = useAppDispatch();
  const channel = useAppSelector(selectChannel);
  const commandPrefix = useAppSelector(selectCommandPrefix);
  const clipLimit = useAppSelector(selectClipLimit);
  const enabledProviders = useAppSelector(selectProviders);
  const layout = useAppSelector(selectLayout);
  const historyIds = useAppSelector(selectHistoryIds);
  const initialQueueOpen: 'true' | 'false' = `${useAppSelector(selectInitialQueueOpen)}`;
  const ignoredChatters = useAppSelector(selectIgnoredChatters).join('\n');

  console.log(initialQueueOpen);

  const form = useForm({
    initialValues: { channel, commandPrefix, clipLimit, enabledProviders, layout, ignoredChatters, initialQueueOpen },
  });

  return (
    <form
      onSubmit={form.onSubmit((settings) => {
        dispatch(settingsChanged(settings));
        closeModal();
      })}
    >
      <Stack spacing="md">
        <Tabs>
          <Tabs.Tab label="Geral" icon={<Settings size={16} />} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
            <Stack>
              <TextInput
                label="Canal da Twitch"
                description="Canal de chat da Twitch para entrar"
                required
                {...form.getInputProps('channel')}
              />
              <Stack spacing={4}>
                <TextInput
                  label="Prefixo de comando"
                  description="Prefixo para comandos de chat, que podem ser usados por moderadores"
                  required
                  {...form.getInputProps('commandPrefix')}
                />
                <Text size="xs" color="gray">
                  Comandos de exemplo: <Code>{form.values.commandPrefix}open</Code>,{' '}
                  <Code>{form.values.commandPrefix}next</Code>
                </Text>
              </Stack>

              <Select
                required
                label="Estado inicial da fila"
                data={[
                  { value: 'true', label: 'Aberta' },
                  { value: 'false', label: 'Fechada' },
                ]}
                {...form.getInputProps('initialQueueOpen')}
              />
            </Stack>
          </Tabs.Tab>

          <Tabs.Tab label="React" icon={<Slideshow size={16} />} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
            <Stack>
              <Select
                required
                label="Layout da fila"
                data={[
                  { value: 'classic', label: 'Clássico' },
                  { value: 'spotlight', label: 'Destaque' },
                  { value: 'fullscreen', label: 'Tela cheia com popup (experimental)' },
                ]}
                {...form.getInputProps('layout')}
              />
              <Stack spacing="sm">
                <Text size="sm">Provedores de clipes</Text>
                <Chips multiple {...form.getInputProps('enabledProviders')}>
                  <Chip value="twitch-clip">Twitch Clips</Chip>
                  <Chip value="twitch-vod">Twitch Vídeos / VODs</Chip>
                  <Chip value="kick-clip">Kick Clips</Chip>
                  <Chip value="youtube">YouTube</Chip>
                  <Chip value="streamable">Streamable</Chip>
                  <Chip value="afreeca-clip">Afreeca Clips</Chip>
                  <Chip value="tiktok">TikTok</Chip>
                  <Chip value="twitter">Twitter / X</Chip>
                  <Chip value="instagram">Instagram</Chip>
                </Chips>
              </Stack>
              <NumberInput
                label="Limite de clipes"
                description={
                  <>
                    Número máximo de clipes na fila. Após isso, novos clipes não serão aceitos, clipes atuais podem ser
                    impulsionados para o topo da fila. Você pode <em>Pular</em> um clipe ao invés de usar <em>Próximo</em> para
                    liberar um espaço.
                    <br />
                    Deixe vazio ou 0 para desabilitar.
                  </>
                }
                min={0}
                step={1}
                value={form.values.clipLimit ?? undefined}
                onChange={(event) => form.setFieldValue('clipLimit', event ?? null)}
              />
              <Textarea
                label="Usuários ignorados"
                description="Nomes de chat dos usuários para ignorar mensagens, um por linha. Eles não poderão enviar clipes e usar comandos de chat."
                minRows={4}
                autoComplete="off"
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
                {...form.getInputProps('ignoredChatters')}
              />
            </Stack>
          </Tabs.Tab>

          <Tabs.Tab label="Memória de clipes" icon={<History size={16} />} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
            <Stack>
              <Text size="sm">
                Em breve, você poderá configurar por quanto tempo clipes assistidos devem ser lembrados antes de poderem ser
                adicionados à fila novamente. Além de alterar outras configurações relacionadas à memória de clipes.
              </Text>
              <Group>
                <Text size="sm">Você tem {historyIds.length} clipes na memória permanente</Text>
                <Button color="red" size="xs" onClick={() => dispatch(memoryPurged())}>
                  Limpar memória
                </Button>
              </Group>
            </Stack>
          </Tabs.Tab>
        </Tabs>
        <Group position="right" mt="md">
          <Button onClick={() => closeModal()} variant="outline">
            Cancelar
          </Button>
          <Button type="submit">Salvar</Button>
        </Group>
      </Stack>
    </form>
  );
}

const useSettingsModal = () => {
  const modals = useModals();
  const openSettingsModal = () => {
    const id = modals.openModal({
      title: 'Configurações',
      children: <SettingsModal closeModal={() => modals.closeModal(id)} />,
      closeOnClickOutside: false,
      closeOnEscape: false,
      size: 'lg',
    });
  };

  return { openSettingsModal };
};

export default useSettingsModal;
